import { useEffect, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { detectBrand, luhnCheck, formatPan, formatExpiry, parseExpiry } from './cardUtils.js';
import type { CardBrand } from './cardUtils.js';
import { captureCard, WidgetApiError } from './api.js';
import type { CaptureResult, Chain } from './api.js';

// ---------------------------------------------------------------------------
// Países soportados (extensible)
// ---------------------------------------------------------------------------

interface CountryConfig {
  code: 'PR' | 'US' | 'DO';
  name: string;
  /** Valor con el que se pre-rellena el ZIP al elegir el país (sigue siendo editable). */
  defaultZip: string;
  stateLabel: string; // "Pueblo" para PR, "Estado" para US, "Provincia" para DO
}

const COUNTRIES: readonly CountryConfig[] = [
  { code: 'PR', name: 'Puerto Rico', defaultZip: '00901', stateLabel: 'Pueblo' },
  { code: 'US', name: 'Estados Unidos', defaultZip: '', stateLabel: 'Estado' },
  { code: 'DO', name: 'República Dominicana', defaultZip: '51000', stateLabel: 'Provincia' },
];

const DEFAULT_COUNTRY: CountryConfig['code'] = 'PR';

const countryConfig = (code: string): CountryConfig => {
  const found = COUNTRIES.find((c) => c.code === code);
  return found ?? COUNTRIES[0]!;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PaymentFormProps {
  apiBase: string;
  /**
   * Username único. Se usa para provisionar el mailbox en kmail (el balanceador
   * elige el dominio) y como label de la wallet auto-creada. La address de
   * kmail resultante ES el email del user — el widget no genera emails.
   * Si el host lo pasa, pre-rellena el input.
   */
  username?: string;
  /**
   * Cadena de payout: 'ethereum' (default), 'bitcoin' o 'lightning'.
   * Define qué wallet auto-crea el server si no se pasa `walletAddress`.
   */
  chain?: Chain;
  walletAddress?: string;
  /** Callback al guardar con éxito. Recibe id, brand, last4 — NUNCA PAN/CVV. */
  onSuccess?: (result: CaptureResult) => void;
  onError?: (error: WidgetApiError) => void;
}

interface AccountForm {
  username: string;
  firstName: string;
  lastName: string;
  /** Teléfono en formato E.164 (ej. "+18495551234"). */
  phoneE164: string;
  /** Fecha de nacimiento ISO (YYYY-MM-DD). */
  dateOfBirth: string;
}

const PHONE_E164_REGEX = /^\+[1-9]\d{1,14}$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

interface CardForm {
  pan: string;
  cvv: string;
  expiry: string;
  cardholderName: string;
}

interface BillingForm {
  country: CountryConfig['code'];
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

/** Username permitido: minúsculas, dígitos, dot, dash, underscore. 3-32 chars. */
const USERNAME_REGEX = /^[a-z0-9][a-z0-9._-]{2,31}$/;

const INITIAL_CARD: CardForm = { pan: '', cvv: '', expiry: '', cardholderName: '' };
const INITIAL_BILLING: BillingForm = {
  country: DEFAULT_COUNTRY,
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: countryConfig(DEFAULT_COUNTRY).defaultZip,
};

const BRAND_LABEL: Record<CardBrand, string> = {
  visa: 'VISA',
  mastercard: 'MC',
  amex: 'AMEX',
  discover: 'DISC',
  diners: 'DINERS',
  jcb: 'JCB',
  unknown: '',
};

type Step = 'account' | 'card' | 'billing';

export const PaymentForm = ({
  apiBase,
  username: usernameFromProps,
  chain,
  walletAddress,
  onSuccess,
  onError,
}: PaymentFormProps) => {
  // Si el host ya pasó username, saltamos el primer paso.
  // (`email` ya no se pide al cliente — se auto-genera al submit a partir del cardholder.)
  const hasAccountFromProps = Boolean(usernameFromProps?.trim());

  const [step, setStep] = useState<Step>(hasAccountFromProps ? 'card' : 'account');
  const [account, setAccount] = useState<AccountForm>({
    username: usernameFromProps?.trim() ?? '',
    firstName: '',
    lastName: '',
    phoneE164: '',
    dateOfBirth: '',
  });
  const [card, setCard] = useState<CardForm>(INITIAL_CARD);
  const [billing, setBilling] = useState<BillingForm>(INITIAL_BILLING);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<CaptureResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Auto-llenado por username: si el user tipea un username que ya existe en
  // Lpay, traemos sus campos KYC (first/lastName, phone, DOB).
  //
  // `lookupPrefilled` marca si el lookup encontrado realmente pre-llenó algún
  // campo (vs encontró un user sin KYC) — controla si mostramos el hint visual.
  type LookupStatus = 'idle' | 'loading' | 'found' | 'notfound' | 'error';
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>('idle');
  const [lookupPrefilled, setLookupPrefilled] = useState(false);

  const brand = detectBrand(card.pan);
  const cvvMaxLen = brand === 'amex' ? 4 : 3;
  const country = countryConfig(billing.country);

  // -- Username lookup (debounced) ------------------------------------------
  // Si el host ya pasó username via props no buscamos — el step `account` ni se
  // renderiza. Cuando el user tipea un username válido, debounce 500ms + fetch.
  useEffect(() => {
    if (hasAccountFromProps) return;
    const u = account.username.trim();
    if (!USERNAME_REGEX.test(u)) {
      setLookupStatus('idle');
      setLookupPrefilled(false);
      return;
    }
    // Limpiar el resultado previo al entrar a loading.
    setLookupStatus('loading');
    setLookupPrefilled(false);
    // El AbortController vive dentro del setTimeout para evitar una race
    // condition: si el componente se desmonta entre el schedule del timer y
    // su disparo, clearTimeout cancela el fire y no hay fetch que abortar.
    // Si el fire ya ocurrió, la closure abajo expone `controller` al cleanup
    // a través de la variable `pendingController` para que pueda abortarlo.
    let pendingController: AbortController | null = null;
    const timer = setTimeout(() => {
      void (async () => {
      const controller = new AbortController();
      pendingController = controller;
      try {
        // apiBase ya incluye `/api` (resuelto por inferApiBase). Sumamos solo
        // el resto del path para no duplicar el prefix.
        const r = await fetch(
          `${apiBase}/widget/users/by-username?username=${encodeURIComponent(u)}`,
          { signal: controller.signal },
        );
        if (r.status === 404) {
          setLookupStatus('notfound');
          return;
        }
        if (!r.ok) {
          setLookupStatus('error');
          return;
        }
        const j = (await r.json()) as {
          data: {
            firstName: string | null;
            lastName: string | null;
            phoneE164: string | null;
            dateOfBirth: string | null;
          };
        };
        const user = j.data;
        // Track si al menos un campo KYC vino con valor — usado para mostrar
        // el hint visual solo cuando realmente hubo pre-fill.
        // `||` intencional: chequeo de truthiness (un '' no cuenta como prefill).
        const anyPrefilled = Boolean(
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          user.firstName || user.lastName || user.phoneE164 || user.dateOfBirth,
        );
        setLookupPrefilled(anyPrefilled);
        // Pre-llenar solo lo que el user todavía no tipeó (no pisar input manual).
        setAccount((prev) => ({
          username: prev.username,
          firstName: prev.firstName || (user.firstName ?? ''),
          lastName: prev.lastName || (user.lastName ?? ''),
          phoneE164: prev.phoneE164 || (user.phoneE164 ?? ''),
          dateOfBirth: prev.dateOfBirth || (user.dateOfBirth ?? ''),
        }));
        // Limpiar errores de los fields que acabamos de llenar.
        setErrors((prev) => ({
          ...prev,
          firstName: user.firstName ? undefined : prev.firstName,
          lastName: user.lastName ? undefined : prev.lastName,
          phoneE164: user.phoneE164 ? undefined : prev.phoneE164,
          dateOfBirth: user.dateOfBirth ? undefined : prev.dateOfBirth,
        }));
        setLookupStatus('found');
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setLookupStatus('error');
      }
      })();
    }, 500);
    return () => {
      clearTimeout(timer);
      // Si el timer ya disparó y hay un fetch en vuelo, abortarlo.
      pendingController?.abort();
    };
  }, [account.username, apiBase, hasAccountFromProps]);

  // -- Update helpers -------------------------------------------------------

  const updateCard =
    <K extends keyof CardForm>(key: K) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      let v = e.target.value;
      if (key === 'pan') v = formatPan(v);
      else if (key === 'expiry') v = formatExpiry(v);
      else if (key === 'cvv') v = v.replace(/\D/g, '').slice(0, cvvMaxLen);
      setCard((prev) => ({ ...prev, [key]: v }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const updateBilling =
    <K extends keyof BillingForm>(key: K) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      setBilling((prev) => ({ ...prev, [key]: e.target.value }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const code = e.target.value as CountryConfig['code'];
    const conf = countryConfig(code);
    setBilling((prev) => ({ ...prev, country: code, zip: conf.defaultZip }));
    setErrors((prev) => ({ ...prev, country: undefined, zip: undefined }));
  };

  // -- Validation -----------------------------------------------------------

  const updateAccount =
    <K extends keyof AccountForm>(key: K) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const v = key === 'username' ? e.target.value.trim().toLowerCase() : e.target.value;
      setAccount((prev) => ({ ...prev, [key]: v }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const validateAccount = (): boolean => {
    const e: Record<string, string> = {};
    if (!account.username.trim()) e.username = 'Username requerido';
    else if (!USERNAME_REGEX.test(account.username.trim())) {
      e.username = '3-32 chars: minúsculas, dígitos, . _ -';
    }
    if (!account.firstName.trim()) e.firstName = 'Nombre requerido';
    if (!account.lastName.trim()) e.lastName = 'Apellido requerido';
    if (!account.phoneE164.trim()) e.phoneE164 = 'Teléfono requerido';
    else if (!PHONE_E164_REGEX.test(account.phoneE164.trim())) {
      e.phoneE164 = 'Formato E.164: +<código país><número>, ej. +18495551234';
    }
    if (!account.dateOfBirth.trim()) e.dateOfBirth = 'Fecha de nacimiento requerida';
    else if (!ISO_DATE_REGEX.test(account.dateOfBirth.trim())) {
      e.dateOfBirth = 'Formato YYYY-MM-DD';
    } else {
      const d = new Date(`${account.dateOfBirth.trim()}T00:00:00Z`);
      if (Number.isNaN(d.getTime())) e.dateOfBirth = 'Fecha inválida';
      else if (d >= new Date()) e.dateOfBirth = 'La fecha debe ser pasada';
      else if (d < new Date('1900-01-01T00:00:00Z')) e.dateOfBirth = 'Año demasiado antiguo';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateCard = (): boolean => {
    const e: Record<string, string> = {};
    if (!luhnCheck(card.pan)) e.pan = 'Número de tarjeta inválido';
    if (card.cvv.length < 3) e.cvv = 'CVV requerido';
    const exp = parseExpiry(card.expiry);
    if (!exp.ok) e.expiry = exp.error;
    if (!card.cardholderName.trim()) e.cardholderName = 'Nombre requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateBilling = (): boolean => {
    const e: Record<string, string> = {};
    if (!billing.line1.trim()) e.line1 = 'Dirección requerida';
    if (!billing.city.trim()) e.city = 'Ciudad requerida';
    if (!billing.zip.trim()) e.zip = 'Código postal requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // -- Step transitions -----------------------------------------------------

  const handleAccountNext = (): void => {
    setGlobalError(null);
    if (!validateAccount()) return;
    setStep('card');
  };

  const handleCardNext = (): void => {
    setGlobalError(null);
    if (!validateCard()) return;
    setStep('billing');
  };

  const handleBack = (): void => {
    setGlobalError(null);
    if (step === 'billing') setStep('card');
    else if (step === 'card' && !hasAccountFromProps) setStep('account');
  };

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault();
    setGlobalError(null);
    if (!validateBilling()) return;

    // Re-validar account + card en caso de que el host haya pasado props inválidos
    // o el user haya editado tras pasar de step.
    if (!validateAccount()) {
      if (!hasAccountFromProps) setStep('account');
      return;
    }
    const exp = parseExpiry(card.expiry);
    if (!exp.ok) {
      setStep('card');
      setErrors({ expiry: exp.error });
      return;
    }

    const usernameTrim = account.username.trim();
    setSubmitting(true);
    try {
      const result = await captureCard(apiBase, {
        username: usernameTrim,
        ...(chain ? { chain } : {}),
        ...(walletAddress ? { walletAddress } : {}),
        // KYC opcional — sólo se envía si el step account está habilitado
        // (cuando el host no pasó username vía props, el user completó el form).
        ...(hasAccountFromProps
          ? {}
          : {
              firstName: account.firstName.trim(),
              lastName: account.lastName.trim(),
              phoneE164: account.phoneE164.trim(),
              dateOfBirth: account.dateOfBirth.trim(),
            }),
        card: {
          pan: card.pan,
          cvv: card.cvv,
          expMonth: exp.month,
          expYear: exp.year,
          cardholderName: card.cardholderName.trim(),
        },
        billing: {
          line1: billing.line1.trim(),
          line2: billing.line2.trim() ? billing.line2.trim() : null,
          city: billing.city.trim(),
          state: billing.state.trim() ? billing.state.trim() : null,
          zip: billing.zip.trim(),
          country: billing.country,
        },
      });
      setSuccess(result);
      onSuccess?.(result);
      // Limpiar campos sensibles
      setCard(INITIAL_CARD);
      setBilling(INITIAL_BILLING);
      setStep(hasAccountFromProps ? 'card' : 'account');
    } catch (err) {
      const apiErr =
        err instanceof WidgetApiError
          ? err
          : new WidgetApiError(0, 'UNKNOWN', err instanceof Error ? err.message : 'Error');
      setGlobalError(apiErr.message);
      onError?.(apiErr);
    } finally {
      setSubmitting(false);
    }
  };

  // -- Success view ---------------------------------------------------------

  if (success) {
    return (
      <div className="wp-card">
        <div className="wp-banner wp-banner-success">
          <strong>Tarjeta guardada ✓</strong>
          <div style={{ marginTop: 4 }}>
            {success.brand.toUpperCase()} •••• {success.last4}
          </div>
        </div>
        <button
          type="button"
          className="wp-button"
          onClick={() => {
            setSuccess(null);
          }}
        >
          Guardar otra tarjeta
        </button>
      </div>
    );
  }

  // -- Form render ----------------------------------------------------------

  // Journey completo del cliente = 5 pasos: Cuenta, Tarjeta, Dirección (este
  // form) + Verificación facial (paso 4) + Listo (paso 5). Esos últimos dos los
  // renderiza BiometricVerification, pero el stepper los cuenta para que el
  // cliente vea el progreso real "de 5".
  const totalSteps = 5;
  const stepNumber: number = step === 'account' ? 1 : step === 'card' ? 2 : 3;
  const stepLabel: string =
    step === 'account' ? 'Cuenta' : step === 'card' ? 'Tarjeta' : 'Dirección de cobro';

  return (
    <form className="wp-card" onSubmit={(e) => void handleSubmit(e)}>
      <div className="wp-header">
        <h2 className="wp-title">Datos de pago</h2>
        <p className="wp-subtitle">
          Paso {stepNumber} de {totalSteps} · {stepLabel}
        </p>
      </div>

      {globalError ? <div className="wp-banner wp-banner-error">{globalError}</div> : null}

      {step === 'account' ? (
        <>
          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-username">
              Username
            </label>
            <input
              id="wp-username"
              className={`wp-input${errors.username ? ' wp-error' : ''}`}
              type="text"
              autoComplete="username"
              placeholder="jdoe"
              value={account.username}
              onChange={updateAccount('username')}
              disabled={submitting}
              maxLength={32}
            />
            {errors.username ? <span className="wp-error-text">{errors.username}</span> : null}
            {lookupStatus === 'loading' ? (
              <span className="wp-hint">Buscando…</span>
            ) : null}
            {lookupStatus === 'found' && lookupPrefilled ? (
              <span className="wp-hint wp-hint-success">
                ✓ Te encontramos — pre-llenamos tus datos. Verificá y continuá.
              </span>
            ) : null}
          </div>

          <div className="wp-row">
            <div className="wp-field">
              <label className="wp-label" htmlFor="wp-firstName">
                Nombre
              </label>
              <input
                id="wp-firstName"
                className={`wp-input${errors.firstName ? ' wp-error' : ''}`}
                type="text"
                autoComplete="given-name"
                value={account.firstName}
                onChange={updateAccount('firstName')}
                disabled={submitting}
                maxLength={100}
              />
              {errors.firstName ? (
                <span className="wp-error-text">{errors.firstName}</span>
              ) : null}
            </div>
            <div className="wp-field">
              <label className="wp-label" htmlFor="wp-lastName">
                Apellido
              </label>
              <input
                id="wp-lastName"
                className={`wp-input${errors.lastName ? ' wp-error' : ''}`}
                type="text"
                autoComplete="family-name"
                value={account.lastName}
                onChange={updateAccount('lastName')}
                disabled={submitting}
                maxLength={100}
              />
              {errors.lastName ? <span className="wp-error-text">{errors.lastName}</span> : null}
            </div>
          </div>

          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-phoneE164">
              Teléfono (E.164)
            </label>
            <input
              id="wp-phoneE164"
              className={`wp-input${errors.phoneE164 ? ' wp-error' : ''}`}
              type="tel"
              autoComplete="tel"
              placeholder="+18495551234"
              value={account.phoneE164}
              onChange={updateAccount('phoneE164')}
              disabled={submitting}
              maxLength={20}
            />
            {errors.phoneE164 ? <span className="wp-error-text">{errors.phoneE164}</span> : null}
          </div>

          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-dateOfBirth">
              Fecha de nacimiento
            </label>
            <input
              id="wp-dateOfBirth"
              className={`wp-input${errors.dateOfBirth ? ' wp-error' : ''}`}
              type="date"
              autoComplete="bday"
              value={account.dateOfBirth}
              onChange={updateAccount('dateOfBirth')}
              disabled={submitting}
              max={new Date().toISOString().slice(0, 10)}
              min="1900-01-01"
            />
            {errors.dateOfBirth ? (
              <span className="wp-error-text">{errors.dateOfBirth}</span>
            ) : null}
          </div>

          <button
            type="button"
            className="wp-button"
            onClick={handleAccountNext}
            disabled={submitting}
          >
            Continuar →
          </button>
        </>
      ) : step === 'card' ? (
        <>
          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-fld-n">
              Número de tarjeta
            </label>
            <div className="wp-pan-wrapper">
              <input
                id="wp-fld-n"
                name="fld-n"
                className={`wp-input${errors.pan ? ' wp-error' : ''}`}
                type="text"
                inputMode="numeric"
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore=""
                placeholder="1234 5678 9012 3456"
                value={card.pan}
                onChange={updateCard('pan')}
                disabled={submitting}
                maxLength={23}
              />
              <span className="wp-brand-icon">{BRAND_LABEL[brand]}</span>
            </div>
            {errors.pan ? <span className="wp-error-text">{errors.pan}</span> : null}
          </div>

          <div className="wp-row">
            <div className="wp-field">
              {/* IDs/labels/names ofuscados para que Chrome NO clasifique como cc-exp/cc-csc.
                  Sin esa clasificación heurística, no aparece la advertencia "autofill desactivado
                  porque la conexión no es segura" en HTTP. */}
              <label className="wp-label" htmlFor="wp-fld-d">
                Vencimiento
              </label>
              <input
                id="wp-fld-d"
                name="fld-d"
                className={`wp-input${errors.expiry ? ' wp-error' : ''}`}
                type="text"
                inputMode="numeric"
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore=""
                placeholder="MM/AA"
                value={card.expiry}
                onChange={updateCard('expiry')}
                disabled={submitting}
                maxLength={7}
              />
              {errors.expiry ? <span className="wp-error-text">{errors.expiry}</span> : null}
            </div>
            <div className="wp-field">
              <label className="wp-label" htmlFor="wp-fld-s">
                CVV
              </label>
              <input
                id="wp-fld-s"
                name="fld-s"
                className={`wp-input${errors.cvv ? ' wp-error' : ''}`}
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore=""
                placeholder={brand === 'amex' ? '4 dígitos' : '3 dígitos'}
                value={card.cvv}
                onChange={updateCard('cvv')}
                disabled={submitting}
                maxLength={cvvMaxLen}
              />
              {errors.cvv ? <span className="wp-error-text">{errors.cvv}</span> : null}
            </div>
          </div>

          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-fld-h">
              Nombre del titular
            </label>
            <input
              id="wp-fld-h"
              name="fld-h"
              className={`wp-input${errors.cardholderName ? ' wp-error' : ''}`}
              type="text"
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore=""
              value={card.cardholderName}
              onChange={updateCard('cardholderName')}
              disabled={submitting}
            />
            {errors.cardholderName ? (
              <span className="wp-error-text">{errors.cardholderName}</span>
            ) : null}
          </div>

          {!hasAccountFromProps ? (
            <div className="wp-row">
              <button
                type="button"
                className="wp-button wp-button-secondary"
                onClick={handleBack}
                disabled={submitting}
              >
                ← Volver
              </button>
              <button
                type="button"
                className="wp-button"
                onClick={handleCardNext}
                disabled={submitting}
              >
                Continuar →
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="wp-button"
              onClick={handleCardNext}
              disabled={submitting}
            >
              Continuar →
            </button>
          )}
        </>
      ) : (
        <>
          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-country">
              País
            </label>
            <select
              id="wp-country"
              className="wp-select"
              value={billing.country}
              onChange={handleCountryChange}
              disabled={submitting}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-line1">
              Dirección
            </label>
            <input
              id="wp-line1"
              className={`wp-input${errors.line1 ? ' wp-error' : ''}`}
              type="text"
              autoComplete="address-line1"
              value={billing.line1}
              onChange={updateBilling('line1')}
              disabled={submitting}
            />
            {errors.line1 ? <span className="wp-error-text">{errors.line1}</span> : null}
          </div>

          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-line2">
              Dirección 2 (opcional)
            </label>
            <input
              id="wp-line2"
              className="wp-input"
              type="text"
              autoComplete="address-line2"
              value={billing.line2}
              onChange={updateBilling('line2')}
              disabled={submitting}
            />
          </div>

          <div className="wp-row">
            <div className="wp-field">
              <label className="wp-label" htmlFor="wp-city">
                Ciudad
              </label>
              <input
                id="wp-city"
                className={`wp-input${errors.city ? ' wp-error' : ''}`}
                type="text"
                autoComplete="address-level2"
                value={billing.city}
                onChange={updateBilling('city')}
                disabled={submitting}
              />
              {errors.city ? <span className="wp-error-text">{errors.city}</span> : null}
            </div>
            <div className="wp-field">
              <label className="wp-label" htmlFor="wp-state">
                {country.stateLabel}
              </label>
              <input
                id="wp-state"
                className="wp-input"
                type="text"
                autoComplete="address-level1"
                value={billing.state}
                onChange={updateBilling('state')}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="wp-field">
            <label className="wp-label" htmlFor="wp-zip">
              Código postal
            </label>
            <input
              id="wp-zip"
              className={`wp-input${errors.zip ? ' wp-error' : ''}`}
              type="text"
              autoComplete="postal-code"
              value={billing.zip}
              onChange={updateBilling('zip')}
              disabled={submitting}
            />
            {errors.zip ? <span className="wp-error-text">{errors.zip}</span> : null}
          </div>

          <div className="wp-row">
            <button
              type="button"
              className="wp-button wp-button-secondary"
              onClick={handleBack}
              disabled={submitting}
            >
              ← Volver
            </button>
            <button type="submit" className="wp-button" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="wp-spinner"></span>Guardando…
                </>
              ) : (
                'Guardar tarjeta'
              )}
            </button>
          </div>
        </>
      )}

    </form>
  );
};
